/**
 * Events and RSVP (1.3.0) — the Director's two Excel RSVP managers as the app. REQUIREMENTS.md §7,
 * docs/SCHEMA-V4-ADDITIONS.md §2–3, docs/DESIGN-DECISIONS-v4.md "Events and RSVP".
 *
 * Pure rules shared by the API and the app: the roster's vocabularies (Accessibility, Transportation, the
 * person types), the New event and RSVP schemas, how spots are given (Everyone up to the room · First come up
 * to N · Raffle) and the counts. Nothing here reads or writes a list. Who holds a place, the numbers of an event
 * and the draw are in ./event-places (1.7.4).
 *
 * The RSVP asks only: coming or not, how many from the household, guests (and their names, optional), in a raffle
 * whether one more person from the household would like a ticket, accessibility, transportation and notes. There is no food question of any kind (liability) — a test fails if one appears.
 */
import { z } from 'zod';
import { isDateKey } from './dates';
import { vocab } from './vocab';
import { DestinationSchema } from './volunteers';
import { CalendarInputShape, repeatProblem, type CalendarExtras, type CalendarSettings, type EventHelpers, type EventReminders, type SeriesResult } from './event-calendar';
import type { EventAnnounceState } from './event-announce';
import type { EventKind1025 } from './event-kind-1025';
import type { WheelchairState } from './event-wheelchair-1025';
import { DropReasonSchema } from './event-dropouts';
import { DrawAgainSchema, type DrawRecord, type EventNumbers, type GuestsAllowed, type HandMark, type ByHandState, type NumbersChange, type PlaceStatus, type PlateCounts, type RsvpRole, type ThreeCounts } from './event-places';

type RV = typeof vocab.SM_EventRSVPs;
export type Accessibility = RV['Accessibility'][number];
export type Transportation = RV['Transportation'][number];
export type PersonType = RV['PersonType'][number];
export type Coming = RV['Coming'][number];
export type RaffleOutcome = RV['RaffleOutcome'][number];
export type CallOutcome = RV['CallOutcome'][number];
export type SpotRuleChoice = (typeof vocab.SM_Events.SpotRule)[number];
export type EventStatus = (typeof vocab.SM_Events.EventStatus)[number];

/** The roster's lists, exactly as the SM_EventRSVPs columns store them (the workbook's "Special Assistance" / "Other" go to the notes — D-1.3.0-3). */
export const ACCESSIBILITY: readonly Accessibility[] = vocab.SM_EventRSVPs.Accessibility;
export const TRANSPORTATION: readonly Transportation[] = vocab.SM_EventRSVPs.Transportation;
export const PERSON_TYPES: readonly PersonType[] = vocab.SM_EventRSVPs.PersonType;
export const CALL_OUTCOMES = ['Confirmed', 'No Answer', 'Declined', 'Left a Message'] as const satisfies readonly CallOutcome[];

/**
 * 1.5.2 — households by number: "How many from your household?" is capped at the event's per-household limit
 * (default two — "two tickets per household") and never more than the household's size (the tenant record's
 * Household size, 1–4, default 1). After the RSVP close, "Offer extra spots" raises one household's limit.
 */
export const DEFAULT_PER_HOUSEHOLD = 2;
export function householdCap(perHousehold: number | null | undefined, size: number, extra = 0): number {
  const per = perHousehold && perHousehold > 0 ? perHousehold : DEFAULT_PER_HOUSEHOLD;
  return Math.max(1, Math.min(Math.max(1, size), per + Math.max(0, extra)));
}

/** Guests beyond the household: past two is a warning, never a block. */
export const GUEST_SOFT_CAP = 2;
/** The auditorium (the room the Everyone rule defaults to). */
export const AUDITORIUM_CAPACITY = 287;
export const AUDITORIUM = 'Selis Manor Auditorium';

export const SPOT_RULES = ['everyone', 'first', 'raffle'] as const;
export type SpotRule = (typeof SPOT_RULES)[number];
export const SPOT_RULE_CHOICE: Record<SpotRule, SpotRuleChoice> = { everyone: 'Everyone', first: 'First Come', raffle: 'Raffle' };
export const spotRuleOf = (choice: string): SpotRule => (choice === 'First Come' ? 'first' : choice === 'Raffle' ? 'raffle' : 'everyone');

export const AUDIENCES = ['tenants', 'eventSupport', 'allVolunteers'] as const;
export type AudienceKey = (typeof AUDIENCES)[number];

/* ------------------------------------------------------------------------------------------------
 * Schemas
 * ---------------------------------------------------------------------------------------------- */

const text = (max: number) => z.string().trim().max(max);
const optText = (max: number) => z.string().trim().max(max).optional().default('');
const dayKey = z.string().refine(isDateKey, 'date');
const minutes = z.number().int().min(6 * 60).max(23 * 60);
const count = (max: number) => z.number().int().min(0).max(max);

export const EventInputSchema = z
  .object({
    name: text(120).min(1, 'required'),
    dayKey,
    minutes,
    endMinutes: minutes.nullable().optional().default(null),
    /** In the building: the room. */
    room: optText(120),
    /** Off-site: the place (approved list or typed) — the walking-directions link like an outing. */
    offSite: z.boolean().default(false),
    place: DestinationSchema.nullable().optional().default(null),
    description: optText(2000),
    rule: z.enum(SPOT_RULES),
    /** Everyone: the room (287 for the auditorium). */
    capacity: count(5000).nullable().optional().default(null),
    /**
     * 22 Oct 2026 (D-1022-NOLIMIT) — "No limit — everyone who says yes has a place". The data has always read an empty Tenant places
     * as no limit (Capacity null; the waiting list never starts), so this stores no new column: it only lets the office say so on
     * purpose instead of typing a big number. With it on the number is ignored and Capacity is left empty.
     */
    noLimit: z.boolean().optional().default(false),
    /** First come: the N. Raffle: the tickets. */
    tickets: count(5000).nullable().optional().default(null),
    maxPerHousehold: count(20).nullable().optional().default(null),
    audience: z.object({ tenants: z.boolean(), volunteers: z.enum(['none', 'eventSupport', 'all']) }),
    /** A group outing with tenants: one CE activity per tenant checked in (VISIONS, 1.2.0). */
    groupOuting: z.boolean().default(false),
    rsvpCloseDayKey: dayKey.nullable().optional().default(null),
    /** 1.7.4 — the numbers: guests a tenant may announce (blank = the Settings default), places set aside for volunteers, and in a raffle the household extra ticket. */
    guestsAllowed: z.union([z.literal(0), z.literal(1), z.literal(2)]).nullable().optional().default(null),
    volunteerPlaces: count(5000).nullable().optional().default(null),
    householdExtra: z.boolean().optional().default(false),
    /** 1.7.5 — something is served or handed out (the RSVP then asks "come down, or bring it to you?"), and what. */
    served: z.boolean().optional().default(false),
    servedWhat: optText(200),
    /** 1.7.7 — the calendar: kind, what to bring, the accessibility line, RSVP on / off, volunteers needed, Repeat, this event's reminders. */
    ...CalendarInputShape,
  })
  .superRefine((e, ctx) => {
    if (e.offSite && !e.place?.name) ctx.addIssue({ code: 'custom', path: ['place'], message: 'required' });
    if (!e.offSite && !e.room) ctx.addIssue({ code: 'custom', path: ['room'], message: 'required' });
    // 1.7.7: an event with no RSVP is on the calendar only — the room's size is not asked
    if (e.rule === 'everyone' && !e.capacity && !e.noLimit && e.rsvp) ctx.addIssue({ code: 'custom', path: ['capacity'], message: 'required' });
    if (e.rule !== 'everyone' && !e.tickets) ctx.addIssue({ code: 'custom', path: ['tickets'], message: 'required' });
    if (!e.audience.tenants && e.audience.volunteers === 'none') ctx.addIssue({ code: 'custom', path: ['audience'], message: 'required' });
    if (e.endMinutes !== null && e.endMinutes <= e.minutes) ctx.addIssue({ code: 'custom', path: ['endMinutes'], message: 'after-start' });
    if (!e.rsvp && e.rule !== 'everyone') ctx.addIssue({ code: 'custom', path: ['rsvp'], message: 'needs-rsvp' });
    if (e.rsvpCloseDayKey && e.rsvpCloseDayKey > e.dayKey) ctx.addIssue({ code: 'custom', path: ['rsvpCloseDayKey'], message: 'after-event' });
    // 1.7.9: there is no "5th Tuesday" repeat (most months have none), and "the last Thursday" starts on a last one
    const repeatNo = e.repeat ? repeatProblem(e.dayKey, e.repeat.every) : null;
    if (repeatNo) ctx.addIssue({ code: 'custom', path: ['repeat', 'every'], message: repeatNo });
  });
export type EventInput = z.input<typeof EventInputSchema>;
export type EventParsed = z.output<typeof EventInputSchema>;

/** The RSVP — the same form on the person's own link and at the desk. These fields and nothing else. */
export const RsvpAnswerSchema = z
  .object({
    coming: z.enum(['yes', 'no']),
    /** From the household, the person included. */
    household: count(20).default(1),
    /** Guests beyond the household (soft cap 2 — a warning past it). */
    guests: count(20).default(0),
    /** 1.5.2: which of the household are coming, by name, when the record has names (optional). */
    members: z.array(text(60)).max(4).optional().default([]),
    /** 1.7.4: the guests' names — optional, "we always encourage names". */
    guestNames: z.array(text(60)).max(2).optional().default([]),
    /** 1.7.4, a raffle with the household extra on: one more person from the household would like a ticket. */
    householdExtra: z.boolean().optional().default(false),
    /** 1.7.5, only on an event where something is served: true = "Please bring it to me (I can't come down)", false = "I'll come down"; left out = not asked. */
    delivery: z.boolean().optional(),
    /** 1.7.9, only with "no" from a volunteer who holds a place ("I can't make it anymore"): why — one of four, and a line. */
    dropReason: DropReasonSchema.optional(),
    /** 1.8.3, only with "yes" from a volunteer after the number is reached: "Yes, add me" to the standby list. Left out = the question was not answered. */
    standby: z.boolean().optional(),
    accessibility: z.array(z.enum(vocab.SM_EventRSVPs.Accessibility)).max(8).default([]),
    transportation: z.enum(vocab.SM_EventRSVPs.Transportation).default('N/A'),
    notes: optText(1000),
  })
  .transform(({ delivery, dropReason, standby, ...a }) => ({
    ...a,
    ...(a.coming === 'yes' && standby === true ? { standby: true as const } : {}),
    ...(a.coming === 'no' && dropReason ? { dropReason } : {}),
    household: a.coming === 'yes' ? Math.max(1, a.household) : 0,
    guests: a.coming === 'yes' ? a.guests : 0,
    members: a.coming === 'yes' ? a.members.filter(Boolean) : [],
    guestNames: a.coming === 'yes' ? a.guestNames.filter(Boolean).slice(0, a.guests) : [],
    householdExtra: a.coming === 'yes' && a.householdExtra,
    ...(a.coming === 'yes' && delivery !== undefined ? { delivery } : {}),
    // "None" alone, or the needs without "None"
    accessibility: a.accessibility.length > 1 ? a.accessibility.filter((x) => x !== 'None') : a.accessibility,
  }));
export type RsvpAnswer = z.input<typeof RsvpAnswerSchema>;
export type RsvpAnswerParsed = z.output<typeof RsvpAnswerSchema>;

/** The fields an RSVP form shows (the no-food test reads this list with the labels). */
export const RSVP_FIELDS = ['coming', 'household', 'guests', 'guestNames', 'householdExtra', 'delivery', 'accessibility', 'transportation', 'notes'] as const;

const phoneish = z.string().trim().max(40);
/** Who the desk is entering an RSVP for: a tenant, a volunteer, a past contact (a special guest), or someone new. */
export const StaffPersonSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('tenant'), tenantId: z.string().trim().min(3).max(50) }),
  z.object({ type: z.literal('volunteer'), volunteerId: z.string().trim().min(3).max(50) }),
  z.object({
    type: z.literal('contact'),
    name: text(120).min(1, 'required'),
    phone: phoneish.optional().default(''),
    email: z.union([z.literal(''), z.string().trim().toLowerCase().max(254).regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, 'email')]).optional().default(''),
    personType: z.enum(['Special Guest', 'Group Volunteer']).default('Special Guest'),
    /** A member or guest of a household: the head's RSVPID. */
    guestOf: z.string().trim().max(40).optional().default(''),
  }),
]);
export type StaffPerson = z.input<typeof StaffPersonSchema>;

/** 25 Oct 2026: `wheelchairGuests` — how many of the guests need a wheelchair spot (the office's pre-pass flag; kept in the event's file, never asked on a public link). */
export const StaffRsvpSchema = z.object({ person: StaffPersonSchema, answer: RsvpAnswerSchema, wheelchairGuests: count(10).optional(), /** 26 Oct 2026: why the desk went past the household limit (administrator and co-administrator only). */ overReason: z.string().trim().max(200).optional() });
export type StaffRsvpInput = z.input<typeof StaffRsvpSchema>;

export const CallLogSchema = z.object({ rsvpId: z.string().trim().min(5).max(40), outcome: z.enum(CALL_OUTCOMES), note: optText(300) });
export type CallLogInput = z.input<typeof CallLogSchema>;

/** 1.7.4: nothing to choose — the draw is random. Drawing again is the administrator's, with a reason. */
export const DrawSchema = DrawAgainSchema;

/** PersonKey (the RSVPKey's second half): T:<TenantID> · V:<VolunteerID> · C:<lower-case name>|<digits of phone or the email>. */
export function personKey(p: { tenantId?: string | null; volunteerId?: string | null; name?: string; phone?: string; email?: string }): string {
  if (p.tenantId) return `T:${p.tenantId}`;
  if (p.volunteerId) return `V:${p.volunteerId}`;
  const name = (p.name ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  const reach = (p.phone ?? '').replace(/\D/g, '') || (p.email ?? '').trim().toLowerCase();
  return `C:${name}|${reach}`;
}
export const rsvpKey = (eventId: string, pk: string) => `${eventId} | ${pk}`.slice(0, 150);

export const guestWarning = (guests: number) => guests > GUEST_SOFT_CAP;

/* ------------------------------------------------------------------------------------------------
 * How spots are given: Everyone (up to the room) and First come (up to N)
 * ---------------------------------------------------------------------------------------------- */

export interface SpotRow {
  id: string;
  /** People this row brings (household + guests). */
  attending: number;
  /** Already given a spot — kept (a yes is not taken back by someone else's answer). */
  holding: boolean;
  /** Current standby rank (1 = next up), or null. */
  standbyRank: number | null;
  /** When the first yes came (ties broken by id). */
  order: string;
}

export interface SpotPlace {
  standby: boolean;
  rank: number | null;
}

/**
 * Who has a spot and who waits. Rows holding a spot keep it. The others — standby first, in rank order, then
 * new answers in the order they came — take a spot while the people fit; a party that does not fit waits
 * (first on standby) and a smaller party behind it may take the spots that are left. `limit` null = no limit.
 */
export function settleSpots(rows: readonly SpotRow[], limit: number | null): Map<string, SpotPlace> {
  const out = new Map<string, SpotPlace>();
  if (limit === null) {
    for (const r of rows) out.set(r.id, { standby: false, rank: null });
    return out;
  }
  let used = rows.filter((r) => r.holding).reduce((s, r) => s + r.attending, 0);
  for (const r of rows) if (r.holding) out.set(r.id, { standby: false, rank: null });
  const waiting = rows
    .filter((r) => !r.holding)
    .sort((a, b) => (a.standbyRank ?? Number.MAX_SAFE_INTEGER) - (b.standbyRank ?? Number.MAX_SAFE_INTEGER) || a.order.localeCompare(b.order) || a.id.localeCompare(b.id));
  let rank = 0;
  for (const r of waiting) {
    if (used + r.attending <= limit) {
      used += r.attending;
      out.set(r.id, { standby: false, rank: null });
    } else out.set(r.id, { standby: true, rank: ++rank });
  }
  return out;
}

/* ------------------------------------------------------------------------------------------------
 * Counts
 * ---------------------------------------------------------------------------------------------- */

export interface CountRow {
  personType: string;
  coming: string;
  standby: boolean;
  household: string;
  householdCount: number;
  guestCount: number;
  attending: number;
  raffleOutcome: string;
  checkedIn: boolean;
  /** A staff row (staff unit, office — TenantCategory Staff): in the room, never a tenant household (1.4.0 §4). */
  staff?: boolean;
  /** 1.7.4: has a place (guaranteed, winner, going) — when given, it decides; when absent the old reading stands. */
  holds?: boolean;
}

export interface EventCounts {
  /** People with a spot (the raffle: tickets won once drawn, asked before). */
  attending: number;
  households: number;
  guests: number;
  volunteers: number;
  standby: number;
  noAnswer: number;
  no: number;
  checkedIn: number;
  /** 1.5.8 (Past events): everyone with a line on the event — the people it was for — and how many said yes. */
  asked: number;
  saidYes: number;
}

export function eventCounts(rows: readonly CountRow[], rule: SpotRule, drawn: boolean): EventCounts {
  const yes = rows.filter((r) => r.coming === 'Yes');
  const holding = yes.filter((r) => (r.holds !== undefined ? r.holds : rule === 'raffle' && drawn ? r.raffleOutcome === 'Winner' : !r.standby));
  const tenants = holding.filter((r) => r.personType === 'Tenant' && !r.staff);
  return {
    attending: holding.reduce((s, r) => s + r.attending, 0),
    households: new Set(tenants.map((r) => r.household)).size,
    guests: holding.reduce((s, r) => s + r.guestCount, 0) + holding.filter((r) => r.personType === 'Special Guest').reduce((s, r) => s + r.householdCount, 0),
    volunteers: holding.filter((r) => r.personType === 'Individual Volunteer' || r.personType === 'Group Volunteer').reduce((s, r) => s + r.householdCount, 0),
    standby: yes.filter((r) => (r.holds !== undefined ? !r.holds && (r.standby || r.raffleOutcome === 'Standby') : rule === 'raffle' && drawn ? r.raffleOutcome === 'Standby' : r.standby)).length,
    noAnswer: rows.filter((r) => r.coming === 'No Answer').length,
    no: rows.filter((r) => r.coming === 'No' || r.coming === 'Withdrawn').length,
    checkedIn: rows.filter((r) => r.checkedIn).length,
    asked: rows.length,
    saidYes: yes.length,
  };
}

/* ------------------------------------------------------------------------------------------------
 * API shapes
 * ---------------------------------------------------------------------------------------------- */

/** One draw as it is kept (1.7.4): the pool, the order, who and when, the fingerprint. Nothing in it can run the draw again. */
export type RaffleDraw = DrawRecord;

export interface CallEntry {
  at: string;
  by: string;
  outcome: CallOutcome;
  note: string;
}

export interface RsvpRowView {
  rsvpId: string;
  personType: PersonType;
  name: string;
  unit: string;
  /** Office only (the Summary, the call list); never on the Volunteer List. */
  phone: string;
  tenantId: string | null;
  volunteerId: string | null;
  household: string;
  coming: Coming;
  householdCount: number;
  guestCount: number;
  attending: number;
  accessibility: Accessibility[];
  transportation: Transportation;
  notes: string;
  standby: boolean;
  standbyRank: number | null;
  raffleOutcome: RaffleOutcome;
  tickets: number;
  raffleRank: number | null;
  checkedIn: boolean;
  checkedInAt: string | null;
  confirmedByPhone: boolean;
  callOutcome: CallOutcome;
  calledAt: string | null;
  calledBy: string | null;
  calls: CallEntry[];
  entry: 'Link' | 'Staff Entered' | 'Imported';
  enteredBy: string | null;
  respondedAt: string | null;
  winnerEmailQueued: boolean;
  /** 25 Oct 2026 — people on this line who need a wheelchair spot (the tag counts one, plus the guests the office flagged), and how many of those are the flagged guests. */
  wheelchairRiders?: number;
  wheelchairGuests?: number;
  /** 1.7.0 — by which link the answer came: the person's own (the email), the open one (the flyer's QR), the desk, or the roster. */
  via: RsvpVia;
  /** 1.7.0 — an open-link answer that matched no tenant record: it waits for the office (attach · keep · ignore). */
  review: boolean;
  /** 1.7.0 — the email or phone typed on the open link (never written to the tenant's record). */
  reach: string;
  lastChangedAt: string | null;
  /** 1.7.4 — a tenant (with their guests) or a volunteer; a volunteer is never counted as a tenant. */
  role: RsvpRole;
  /** 1 Nov 2026 (D-1102-STAFF): a staff member or colleague volunteering — a volunteer line with no volunteer record; never emailed by the app. */
  staff?: boolean;
  /** 1.7.4 — where the person stands: guaranteed · winner · going · in the draw · waiting · no · no answer · cancelled. */
  status: PlaceStatus;
  /** Marked Guaranteed by the office: seated before the draw, never drawn. */
  guaranteed: HandMark | null;
  /** Marked Cancelled by the office: the line stays, the place is open. */
  cancelled: HandMark | null;
  /** Added to the event by the office's hand, and as what. */
  byHand: (HandMark & { as: ByHandState }) | null;
  /** A raffle: one more person from the household has a ticket with them (both or neither). */
  householdExtra: boolean;
  /** The guests the tenant announced, when they gave names. */
  guestNames: string[];
  /** The person changed their answer after first giving it ("updated <when>" is lastChangedAt). */
  updated: boolean;
  /** The reason on the line (guaranteed, cancelled, added by hand) — the export's Reason column. */
  reason: string;
  /** 1.7.5 — their plate goes upstairs: they said yes, will not be there in person, and someone brings their things up. Always false on an event where nothing is served. */
  needsDelivery: boolean;
  /** Who said so: the person's own answer, or the office (with its reason, who and when). Null when not set. */
  delivery: { by: 'answer' | 'office'; mark: HandMark | null } | null;
  /** 1.5.2 — who from the household is coming, by name, when the record has names (the printouts use them). */
  memberNames: string[];
}

export type RsvpVia = 'personal' | 'open' | 'staff' | 'imported';

/** 1.7.0 — the campaign in numbers: "Invited 128 · answered 41 · yes 30 (household 44 + guests 12) · no 11". */
export interface CampaignCounts {
  invited: number;
  /** Rows with a yes or a no. */
  answered: number;
  yes: number;
  /** People from the households that said yes, and their guests (waiting list included). */
  householdPeople: number;
  guestPeople: number;
  no: number;
  /** Answers on the waiting list (rows). */
  waiting: number;
  /** Open-link answers waiting for review. */
  unmatched: number;
  /** The room, the N or the tickets; null = no limit. Taken = people with a spot; left = what remains. */
  limit: number | null;
  taken: number;
  left: number | null;
}

export interface EventView {
  eventId: string;
  name: string;
  dayKey: string;
  minutes: number | null;
  endMinutes: number | null;
  place: string;
  offSite: boolean;
  address: string;
  directions: { google: string; apple: string } | null;
  description: string;
  rule: SpotRule;
  capacity: number | null;
  tickets: number | null;
  maxPerHousehold: number | null;
  audience: { tenants: boolean; volunteers: 'none' | 'eventSupport' | 'all' };
  status: EventStatus;
  groupOuting: boolean;
  /** Made from Volunteers → Schedule an outing (1.1.0): volunteers answer "I'm interested" there. */
  outing: boolean;
  rsvpCloseDayKey: string | null;
  folder: string;
  past: boolean;
  counts: EventCounts;
  /** Everyone: attending past the room (the count turns red). */
  over: boolean;
  invited: { at: string | null; count: number };
  raffle: { drawnAt: string | null; drawnBy: string | null; /** People in the pool (now, or at the draw). */ poolSize: number; /** The last tenants' draw's fingerprint. */ fingerprint: string | null; history: RaffleDraw[] } | null;
  /** 1.7.4 — the numbers of this event, what changed (newest last) and the three counts. */
  numbers: EventNumbers;
  changes: NumbersChange[];
  three: ThreeCounts;
  /** The volunteers' own draw, when it was run. */
  volunteerDraw: { at: string; by: string } | null;
  /** 1.7.5 — something is served or handed out at this event, and what ("what we serve" — the delivery list's "what to bring"). */
  served: { on: boolean; what: string };
  /** 1.7.5 — "22 yes · 3 of them upstairs · 19 seats", in people; null when nothing is served. */
  plates: PlateCounts | null;
  /** Who still needs a call: raffle winners not confirmed by phone, then invited people with no answer. */
  toCall: number;
  /** Winners whose email is not queued yet (Withdraw prepares them). */
  winnersToEmail: number;
  /** 1.7.0 — the open link (`/e/<slug>`): null when the event is not for tenants. */
  openLink: { slug: string; on: boolean } | null;
  campaign: CampaignCounts;
  /** 1.5.1 — a workshop: who runs it, and how many tenants the BCC invitation reached. */
  workshop?: { runBy: 'tech' | 'staff' | 'volunteer'; runnerName: string; invitedTenants: number; invitedAt: string | null } | null;
  /** 1.7.7 — the calendar's part: kind, what to bring, the accessibility line, RSVP on / off, the series, the reminder choice. */
  cal: CalendarExtras;
  /** 1.7.7 — the answer to New event with a Repeat: every event it made. */
  series?: SeriesResult;
  /** 25 Oct 2026 — In-house · Outing · Raffle · Calendar only (the file's answer, else what the facts give). */
  eventKind?: EventKind1025;
  /** 25 Oct 2026 — wheelchair spots: set aside, taken, left (./event-wheelchair-1025). */
  wheelchair?: WheelchairState;
  /** 26 Oct 2026 (D-1026-HOUSEHOLD) — how a household is counted: the whole household, or limited tickets (N per household). */
  count?: { mode: 'household' | 'limited'; perHousehold: number };
}

export interface EventsResponse {
  upcoming: EventView[];
  past: EventView[];
  history: (RaffleDraw & { eventId: string; eventName: string; eventDayKey: string })[];
  todayKey: string;
  /** 1.7.7 — the kinds, the rooms and the reminder defaults (Settings → Events and RSVPs). */
  calendar: CalendarSettings;
}

export interface EventDetail {
  event: EventView;
  rows: RsvpRowView[];
  /** 1.7.0 — the open link's full address on the app's own address (what the QR code holds). */
  openUrl?: string | null;
  /** 13 Oct 2026: the volunteers' own open link (`/e/<slug>/v`), when the event takes volunteers. */
  volunteerUrl?: string | null;
  /** Whether the open links are on (the one switch covers both sides). */
  volunteerLinkOn?: boolean;
  /** 1.7.7 — the scheduled reminders of this event, and the volunteers who said they can help. */
  reminders?: EventReminders;
  helpers?: EventHelpers;
  /** 1.7.9 — Announce this event: the first row of the Reminders card. */
  announce?: EventAnnounceState;
  /** 25 Oct 2026 — an outing made in Volunteers: what its own file says about volunteers (display only). Null on any other event. */
  outingVolunteers?: OutingVolunteers | null;
}

/** 25 Oct 2026 — an outing's volunteers as the outing's own file keeps them: how many are needed, who is interested, who stands by. Names only; nothing is assigned here. */
export interface OutingVolunteers {
  /** Needed; null = no number set. */
  needed: number | null;
  /** Hold a place, in the order they answered. */
  interested: { volunteerId: string; name: string }[];
  /** On the standby list, in the order they joined. */
  standby: { volunteerId: string; name: string; offered: boolean }[];
  /** Places open now (needed − interested; 0 without a number). */
  open: number;
}

/** 1.7.0 — Invite, before the press: who gets it by email (only those), who has to be called, where it goes. */
export interface InvitePreview {
  /** 1.9.3: the event has a description and the switch is on — it is added after the greeting of every invitation. */
  descriptionAdded?: boolean;
  withEmail: { tenantId: string; name: string; unit: string; email: string; invited: boolean }[];
  /** No email on file, or email not allowed: the office calls them. */
  without: { tenantId: string; name: string; unit: string; phone: string }[];
  /** Asked for no event emails — left out. */
  noMarketing: number;
  alreadyInvited: number;
  toSend: number;
  /** Invited, with an email, and no answer yet. */
  toRemind: number;
  /** D-1028-ONELANG: of the tenants still to invite, how many have Spanish on their own record (they get the Spanish version; the rest get English). */
  spanish?: number;
  to: string[];
  cc: string[];
  delayMinutes: string;
  /** 1.7.4 — Invite volunteers: how many it reaches, who already has it, who has no address, who opted out. */
  volunteers?: { toSend: number; /** of them, marked Spanish */ spanish?: number; already: number; noEmail: number; optedOut: number };
}

export const InviteOneSchema = z
  .object({
    tenantId: z.string().trim().min(3).max(50).optional(),
    email: z.union([z.literal(''), z.string().trim().toLowerCase().max(254).regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, 'email')]).optional().default(''),
  })
  .refine((v) => !!v.tenantId || !!v.email, { message: 'required', path: ['email'] });
export type InviteOneInput = z.input<typeof InviteOneSchema>;

export const ResolveSchema = z
  .object({
    rsvpId: z.string().trim().min(5).max(40),
    action: z.enum(['attach', 'keep', 'ignore']),
    tenantId: z.string().trim().min(3).max(50).optional(),
    /** 1.7.4: an answer given "as a volunteer" is attached to a volunteer. */
    volunteerId: z.string().trim().min(3).max(50).optional(),
  })
  .refine((v) => v.action !== 'attach' || !!v.tenantId || !!v.volunteerId, { message: 'required', path: ['tenantId'] });
export type ResolveInput = z.input<typeof ResolveSchema>;

export interface EventSendResult {
  queued: number;
  skipped: { name: string; why: string }[];
  sendAt: string | null;
  message: string;
  /** 1.5.4: volunteers who opted out — "n opted out, not sent". */
  optedOut?: number;
  /** 1.5.4: tenants who asked for no event or invitation emails — left out. */
  noMarketing?: number;
  /** 1.7.0: a send to everyone goes out in parts — how many tenants are still to queue (the app presses on until 0). */
  remaining?: number;
}

export interface PersonHit {
  /** 1 Nov 2026 (D-1102-STAFF): `staff` — a signed-in administrator or co-administrator, or an earlier event's staff line (id is its PersonKey). */
  type: 'tenant' | 'volunteer' | 'contact' | 'staff';
  id: string;
  name: string;
  unit: string;
  /** Where the contact came from ("Guest at <event>"), or the volunteer's services. */
  detail: string;
  /** A contact's reach (phone or email) so the desk can tell two of the same name apart. */
  phone: string;
  email: string;
  personType: PersonType;
  /** This person's RSVP on the event, when there is one. */
  rsvpId: string | null;
  defaults: { accessibility: Accessibility[]; transportation: Transportation; notes: string };
}

/** The RSVP page (/link/:token, kind rsvp): the event, the count, and this person's answer. */
export interface RsvpLinkPage {
  state: 'ok' | 'answered' | 'closed' | 'cancelled' | 'not-valid' | 'expired' | 'replaced';
  lang: 'en' | 'es';
  firstName: string;
  personType: PersonType;
  event: {
    name: string;
    dayKey: string;
    minutes: number | null;
    place: string;
    address: string;
    directions: { google: string; apple: string } | null;
    description: string;
    rule: SpotRule;
    /** "142 of 287 so far" (Everyone and First come). */
    soFar: number;
    limit: number | null;
    drawn: boolean;
    /** 1.7.0 — no spot is left: a new yes joins the waiting list (the page says so before the person sends). */
    full?: boolean;
  };
  answer: RsvpAnswerParsed;
  answered: boolean;
  standby: boolean;
  standbyRank: number | null;
  raffleOutcome: RaffleOutcome;
  tickets: number;
  officePhone: string | null;
  /** Household count applies to tenants; a volunteer answers for themselves and their guests. */
  askHousehold: boolean;
  /** 1.5.2: how many from the household may come (the per-household limit, never more than the household), and who. */
  household?: { cap: number; names: string[]; chosen: string[]; /** 26 Oct 2026: where a new answer starts. */ defaultCount?: number };
  /** 1.7.4 — what this page asks: guests (0 = not asked, else up to 1 or 2), and in a raffle the household extra. */
  ask: { guests: GuestsAllowed; householdExtra: boolean; /** 1.7.5 — "Will you come down, or should we bring it to you?" (tenants, on an event where something is served). */ delivery?: boolean; /** 25 Oct 2026 — Transportation is asked only where there is a "there" (an event off-site, or an outing); left out = asked (older answers of the server). */ transport?: boolean; /** 25 Oct 2026 — wheelchair spots set aside and left (null/left out = not counted). */ wheelchair?: { spots: number; left: number } | null };
  role: RsvpRole;
  /** 1.7.7: a volunteer on an event that needs volunteers (Volunteers needed above 0): the page asks whether they can help. */
  helping?: boolean;
  /** 1.7.9: a volunteer who said "I'm available" and holds a place — the page reads "I'm still coming / I can't make it anymore", and the second asks why before it saves. */
  holding?: boolean;
  /** 1.8.3: a volunteer on an event with a number of volunteers needed — the count stops at the number. `open`: a place is free now; `offered`: the office offered this volunteer a place from the standby list. */
  standby183?: { applies: boolean; open: boolean; offered: boolean };
  /** When the answer was last changed (the page says "updated"). */
  updatedAt: string | null;
}

export interface RsvpLinkAction {
  ok: boolean;
  message: string;
  /** 1.8.3: the number is reached — the page asks the standby question (`message` is the question in the person's language); nothing was saved. */
  ask?: 'standby';
  page?: RsvpLinkPage;
  state?: RsvpLinkPage['state'];
}

/**
 * The two buttons on the EVENT_INVITE email (the Director's wording, 1 Oct 2026). Both open the person's own
 * RSVP page (`?a=yes` / `?a=no` starts it at that answer); the answer is sent from the page — a GET never acts.
 */
export const EVENT_BUTTONS = {
  en: { yes: 'Yes, count me in', no: 'No, thank you' },
  es: { yes: 'Sí, cuenten conmigo', no: 'No, gracias' },
} as const;
